import { CommonModule, CurrencyPipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  WritableSignal,
  computed,
  inject,
  input,
  signal,
  output,
  viewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ExpandableComponent } from '../../../shared/expandable-component/expandable.component';
import { IngredientChoice } from '../../models/menu/ingredient-choice.model';
import { OrderableIngredient } from '../../models/menu/orderable-ingredient.model';
import { OrderableQualifier } from '../../models/menu/orderable-qualifier.model';
import { OrderedChoice } from '../../models/ordering/ordered-choice.model';
import { OrderedIngredient } from '../../models/ordering/ordered-ingredient.model';
import { OrderedItem } from '../../models/ordering/ordered-item.model';
import { SideSelectionType } from '../../models/side-selection-type.enum';
import { ConfigService } from '../../services/config.service';
import { ItemEditorService } from '../../services/item-editor.service';
import { ChoiceValidationResult } from '../../services/ordering/choice-validation-result.model';
import { OrderedChoiceService } from '../../services/ordering/ordered-choice.service';
import { QualifierComponent } from './qualifier/qualifier.component';
import { SideSelectionComponent } from './side-selection/side-selection.component';
import { tap } from 'rxjs';
import { OrderableItem } from '../../models/menu/orderable-item.model';
import { TranslocoDirective, TranslocoPipe } from '@ngneat/transloco';
import { CustomCheckboxComponent } from "../../../shared/custom-checkbox/custom-checkbox.component";
import { DependableChoiceComponent } from "../dependable-choice/dependable-choice.component";
import { TooltipStyling } from '../../models/core/tooltip-styles.model';
import { AuditService } from '../../services/audit.service';
import { ExtraUpsell } from '../../models/menu/extra-upssell.model';
import { UpsellType } from '../../models/ordering/upsell-type.enum';
import { ExtraIngredientUpsell } from '../../models/ordering/ordered-item.model';
import { TooltipComponent, TooltipPlacement } from '../../../shared/tooltip/tooltip.component';
import { DependableIngredientMappingService } from '../../services/dependable-ingr.service';
import { SizePrice } from '../../models/menu/size-price.model';

@Component({
  selector: 'cwo-ingredient',
  standalone: true,
  imports: [
    CommonModule,
    ExpandableComponent,
    SideSelectionComponent,
    QualifierComponent,
    FormsModule,
    CurrencyPipe,
    TranslocoDirective,
    CustomCheckboxComponent,
    DependableChoiceComponent,
    TranslocoPipe,
    TooltipComponent
],
  templateUrl: './ingredient.component.html',
  styleUrl: './ingredient.component.scss',
})
export class IngredientComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  tooltipPlacement = TooltipPlacement;
  orderedChoiceService = inject(OrderedChoiceService);
  itemEditorService = inject(ItemEditorService);
  configService = inject(ConfigService);
  private auditService = inject(AuditService);
  private dependableIngredientMappingService = inject(DependableIngredientMappingService);
  private hostEl = inject(ElementRef);
  openTooltip = false;
  closeAllTooltips = {};

  get hasLoyalty(): boolean {
    return !!this.configService.configState().store?.hasLoyalty &&
      !this.configService.configState().store?.hasThirdPartyLoyalty;
  }

  index = input.required<number>();
  ingredient = input.required<OrderableIngredient>();
  choice = input.required<IngredientChoice>();
  orderedChoice = input.required<OrderedChoice>();
  isSpecial = input.required<boolean>();
  orderedItem = input.required<OrderedItem>();
  orderableItem = input.required<OrderableItem>();
  selectedQualifier = signal<string | null>(null);

  public readonly tooltipContentRef = viewChild.required<TemplateRef<any>>('tooltipContent');
  readonly ingredientAdded = output<OrderedItem>();
  ingredientTooltipVisible = input(false);

  tooltipStyling: TooltipStyling = {
    width: 'fit-content'
  }

  dependableChoices = input<IngredientChoice[]>([]);

  selectedSide: SideSelectionType = SideSelectionType.WHOLE;
  selectedSecondSide: SideSelectionType | null = null;
  readonly emptyQualifiers: string[] = [];
  selectedSecondHalfQualifiers: string[] = this.emptyQualifiers;
  sideSelectionType = SideSelectionType;
  initialValue = false;
  orderedIngredient?: OrderedIngredient;
  qualifierDisplayStyle: WritableSignal<'dropdown' | 'flatten'> = signal('dropdown');

  currentQualifier = computed(() => {
    return this.selectedQualifier() || this.getDefaultQualifier();
  });

  private _qualifiers: OrderableQualifier[] = [];
  private getDefaultQualifier(): string {
    const defaultQualifier = this.ingredient().qualifiers.find(q => q.isDefault);
    return defaultQualifier ? defaultQualifier.name : this.ingredient().qualifiers[0]?.name;
  }

  selectQualifier(qualifier: string) {
    this.selectedQualifier.set(qualifier);
  }

  ingredientUpsell = computed<ExtraUpsell | undefined>(() => {
    const ingredient = this.ingredient();
    if (!ingredient?.qualifiers?.length) return undefined;

    let qualifier: OrderableQualifier | undefined;

    const defaultIdx = ingredient.qualifiers.findIndex(q => q.isDefault);
    if (defaultIdx !== -1 && ingredient.qualifiers.length > 1) {
      qualifier = ingredient.qualifiers[defaultIdx + 1];
    } else {
      qualifier = ingredient.qualifiers.find(
        (q: any) => q.price > 0 || (q.priceFactor ?? 0) > 0
      );
    }

    if (!qualifier) return undefined;

    const basePrice =
      ingredient.sizePrices.find(
        (s: SizePrice) => s.size === this.orderedItem()?.size
      )?.price ?? 0;

    const priceDiff = basePrice * (qualifier.priceFactor ?? 1);

    const pointsPerDollar = this.configService.loyaltyState()?.subscriptionsPointsFactors?.[0]?.pointsPerDollar;
    const pointsDiff = pointsPerDollar != null && priceDiff > 0
      ? Math.round(priceDiff * pointsPerDollar)
      : undefined;

    return {
      choice: qualifier.name,
      ingredient: ingredient.name,
      priceDiff,
      pointsDiff,
      qualifier,
    };
  });

  loading = false;
  displayedPrice = computed(()=> {
    if (this.isSpecial()) return undefined;
    const orderedItem = this.itemEditorService.editingItem()?.orderedItem;
    const itemSize = orderedItem?.size;
    const price = this.orderedIngredient?.price;

    if(!price) {
      const sizePrice = this.itemEditorService.editingItem()?.orderableItem?.getIngredient(this.choice().name, this.ingredient().name)?.sizePrices.find(s => s.size === itemSize && s.status === 'AVAILABLE');
      if (!this.configService.configState().store?.halfPricePolicyAsWhole && this.orderedIngredient?.isLeftHalf || this.orderedIngredient?.isRightHalf) {
        return sizePrice?.halfPrice;
      }
      return sizePrice?.price;
    }
     return price;
  });

  readonly itemEditorHeaderRef = viewChild.required('itemEditorHeader', { read: ElementRef });

  private resizeObserver!: ResizeObserver;
  headerHeight = signal<number>(0);

  tooltipTopMargin = computed(() => {
    return this.headerHeight()
  });

  readonly setMessage = output<ChoiceValidationResult>();

  readonly updatedSpecialItem = output<OrderedItem>();
  
  ngOnInit(): void {
    this.orderedIngredient = this.orderedChoice().ingredients.find(ing => ing.ingredient === this.ingredient().name)
    this.qualifierDisplayStyle.set(this.ingredient().qualifiers.length > 3 ? 'dropdown' : 'flatten');
    if(this.orderedIngredient?.isLeftHalf){
      this.selectedSide = SideSelectionType.LEFT;
    } else if(this.orderedIngredient?.isRightHalf){
      this.selectedSide = SideSelectionType.RIGHT;
    }
    if(this.orderedIngredient?.isSecondHalf){
      // Restore the second half selection based on which side the first half is
      if(this.orderedIngredient.isLeftHalf) {
        this.selectedSecondSide = SideSelectionType.RIGHT;
      } else if(this.orderedIngredient.isRightHalf) {
        this.selectedSecondSide = SideSelectionType.LEFT;
      }
    } else {
      this.selectedSecondSide = null;
    }
    this.syncSecondHalfQualifierSelection();
    //this.setIngredientUpsell(this.ingredient());

  }

  ngAfterViewInit(): void {
    const headerEl = document.getElementsByTagName('cwo-item-editor-header')[0];

    if (!headerEl) return;

    this.resizeObserver = new ResizeObserver(() => {
      const newHeight = headerEl.clientHeight;
      if (newHeight !== this.headerHeight()) {
        this.headerHeight.set(newHeight);
      }
    });

    this.resizeObserver.observe(headerEl);

    this.headerHeight.set(headerEl.clientHeight);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['orderedChoice']) {
      this.orderedIngredient = this.orderedChoiceService.getIngredient(
        this.orderedChoice(),
        this.ingredient()
      );
      this.initialValue = !!this.orderedIngredient;
      this.syncSecondHalfQualifierSelection();
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private refocusSelf(): void {
    setTimeout(() => {
      const ingredientControl = this.hostEl.nativeElement.querySelector('.ingredient-name') as HTMLElement;
      ingredientControl?.focus();
    });
  }

  toggleIngredient(value: boolean, inputElement: any, isRadioButton = false) {
    const tempItem = (this.isSpecial() ? this.orderedItem() : this.itemEditorService.editingItem()?.orderedItem)?.copy() as OrderedItem;

    if (tempItem) {
      const hasIngredient = tempItem.hasIngredient(
        this.choice(),
        this.ingredient()
      );

      const shouldAddDependables = isRadioButton ? !value : value;
      if (isRadioButton && shouldAddDependables) {
        this.dependableIngredientMappingService.removeDependentChoices(
          tempItem,
          new Set(this.choice().ingredients.map(i => i.name)),
          this.orderableItem()
        );
      }

      if (this.dependableChoices().length > 0) {
          if(shouldAddDependables){
          for (const dependableChoice of this.dependableChoices()) {
            const orderableChoice = this.orderableItem().getChoice(dependableChoice.name);
            
            if (orderableChoice) {
              const existingChoice = tempItem.choices.find(choice => choice.name === orderableChoice.name);
              if (!existingChoice) {
                const newChoice = this.orderedChoiceService.createOrderedChoice(orderableChoice);
                const defaultIngredients = orderableChoice.ingredients.filter(ingredient => ingredient.isDefault);
                defaultIngredients.forEach(ingredient => {
                  const orderedIng = OrderedIngredient.createFromOrderable(ingredient);
                  if(orderedIng.isDefault){
                    newChoice.ingredients.push(orderedIng);
                  }
                });
                tempItem.choices.push(newChoice);
              }
            }
          }
        } else {
          for (const dependableChoice of this.dependableChoices()) {
            this.dependableIngredientMappingService.removeChoiceAndDescendants(
              tempItem,
              dependableChoice.name,
              this.orderableItem()
            );
          }
        }
      }

      const choice: OrderedChoice = tempItem.getChoice(
        this.orderedChoice().name
      );

      if (
        this.choice().maxIngredients === 1 &&
        this.choice().enforcedIngredients === 1
      ) {
        choice.ingredients.splice(0);
        const orderedIng = new OrderedIngredient();
        orderedIng['ingredient'] = this.ingredient().name;
        orderedIng['isDefault'] = this.ingredient().isDefault;
        choice.ingredients.push(orderedIng);
      } else {
        if (hasIngredient) {
          if (choice) {
            const ingredientIndex = choice.ingredients.findIndex(
              (ingredient) =>
                ingredient['ingredient'] === this.ingredient().name
            );

            if (ingredientIndex !== -1) {
              choice.ingredients.splice(ingredientIndex, 1);
              setTimeout(() => {
                this.selectedSide = SideSelectionType.WHOLE;
                this.selectedSecondSide = null;
                this.selectedSecondHalfQualifiers = this.emptyQualifiers;
              }, 300);
            }
          }
        } else {
          if (choice) {
            const orderedIng = OrderedIngredient.createFromOrderable(this.ingredient());
            choice.ingredients.push(orderedIng);
          }
        }
      }

      const choiceValidator = this.orderedChoiceService.validateChoice(
        choice,
        this.choice()
      );
      this.setMessage.emit(choiceValidator);

      if (!choiceValidator.valid) {
        choice.warnings = [choiceValidator.longMessage];
      } else {
        choice.warnings = [];
      }
      if (
        choiceValidator.maxIngredients &&
        choiceValidator.currentIngredients &&
        choiceValidator.maxIngredients < choiceValidator.currentIngredients
      ) {
        inputElement.checked = !value;
        this.initialValue = !value;
      } else {        
        if(this.isSpecial()){
          this.updatedSpecialItem.emit(tempItem);
           this.auditService.createAudit(() => `${this.initialValue ? 'Ordered' : 'Removed'} ${this.ingredient().name} ingredient from ${this.orderedChoice().name} choice on ${this.orderedItem().item} item`);
          this.refocusSelf();
        } else {
          this.loading = true;
          this.itemEditorService.updateOrderedItem(tempItem)?.pipe(
          tap(()=> {
            this.itemEditorService._userChoices.set(tempItem.choices);
          })
        )?.subscribe({
          next: () => {
            this.loading = false;
           this.auditService.createAudit(() => `${this.initialValue ? 'Ordered' : 'Removed'} ${this.ingredient().name} ingredient from ${this.orderedChoice().name} choice on ${this.orderedItem().item} item`);
            if(value){
              this.ingredientAdded.emit(tempItem);
            }
            this.refocusSelf();
        },
          error: () => {
            this.initialValue = !value;
            inputElement.checked = !value;
            this.loading = false;
          }
        });
        }
      }
    }
  }


  onTooltipClose(value = false) {

    if(value){
      const extraQualifier = this.ingredient().qualifiers.find(q => q.name === this.ingredientUpsell()?.choice);
      if(extraQualifier && !this.orderedIngredient?.qualifiers.includes(extraQualifier.name)){
          this.auditService.createAudit(() => `Added ${extraQualifier.name} qualifier to ${this.ingredient().name} ingredient from ${this.orderedChoice().name} on ${this.orderedItem().item} item via upsell tooltip`);

        const sourceItem = this.isSpecial() ? this.orderedItem() : this.itemEditorService.editingItem()?.orderedItem;
        if (sourceItem && this.ingredientUpsell) {
          const upsold: ExtraIngredientUpsell = {
            ingredient: this.ingredientUpsell()?.ingredient ?? '',
            itemSize: sourceItem.size,
            qualifierName: this.ingredientUpsell()?.qualifier.name ?? '',
            priceDiff: Math.round((this.ingredientUpsell()?.priceDiff ?? 0) * 100),
          };
          if (sourceItem.extraIngredientUpsells) {
            if (!sourceItem.extraIngredientUpsells.some(u => u.ingredient === upsold.ingredient && u.qualifierName === upsold.qualifierName)) {
              sourceItem.extraIngredientUpsells.push(upsold);
            }
          } else {
            sourceItem.extraIngredientUpsells = [upsold];
          }
          sourceItem.isUpsell = true;
          sourceItem.upsellType = UpsellType.EXTRA_INGREDIENT;
        }

        this.toggleQualifier({ 
          qualifiers: [extraQualifier],
          selectedQualifier: undefined
        });
      }
    }
    this.orderableItem().isExtraIngredientTooltipOpenedOnce = true;
  }

  toggleQualifier(event: { qualifiers: OrderableQualifier[], selectedQualifier?: OrderableQualifier }) {
    const qualifiers = event.qualifiers;
    const selectedQual = event?.selectedQualifier;
    if(selectedQual) {
      this._qualifiers = qualifiers;
      const qualAction = qualifiers.find((q: OrderableQualifier) => q.name === selectedQual.name);
      this.auditService.createAudit(() => `${qualAction ? 'Ordered' : 'Removed'} ${selectedQual.name} qualifier from ${this.ingredient().name} ingredient from ${this.orderedChoice().name} on ${this.orderedItem().item} item`);
    } else {
      const missingQual = this._qualifiers.filter(
        (qual) => !qualifiers.includes(qual)
      )[0];
      if (missingQual) {
        this.auditService.createAudit(
          () =>
            `Removed ${missingQual.name} qualifier from ${
              this.ingredient().name
            } ingredient from ${this.orderedChoice().name} on ${
              this.orderedItem().item
            } item`
        );
        this._qualifiers = qualifiers;
      }
    }

    if(qualifiers.length === 0) this.selectedQualifier.set(null);
    
    const tempItem = (this.isSpecial() ? this.orderedItem() : this.itemEditorService.editingItem()?.orderedItem)?.copy();

    const hasIngredient = tempItem.hasIngredient(
      this.choice(),
      this.ingredient()
    );

    const choice: OrderedChoice = tempItem.getChoice(this.orderedChoice().name);

    if (hasIngredient) {
      if (choice) {
        const ingredientName = this.ingredient().name;
        const orderedIng = new OrderedIngredient();
        const qualifierNames: string[] = [];
        qualifiers.map((q) => {
          qualifierNames.push(q.name);
        });

        const existingIngredientIndex = choice.ingredients.findIndex(
          (q) => q.ingredient === ingredientName
        );

        if (existingIngredientIndex !== -1) {
          // Ingredient exists, update the qualifiers
          choice.ingredients[existingIngredientIndex].qualifiers =
            qualifierNames;
        } else {
          orderedIng['ingredient'] = this.ingredient().name;
          orderedIng['isDefault'] = this.ingredient().isDefault;
          orderedIng['isLeftHalf'] = false;
          orderedIng['isRightHalf'] = false;
          orderedIng['qualifiers'] = qualifierNames;
          choice.ingredients.push(orderedIng);
         
        }
        
      }
       
    }
    if(this.isSpecial()){
      this.updatedSpecialItem.emit(tempItem);

    } else {      
      this.itemEditorService.updateOrderedItem(tempItem)?.subscribe();
    }

  }

  toggleSecondHalfQualifier(event: { qualifiers: OrderableQualifier[], selectedQualifier?: OrderableQualifier }) {
    const qualifiers = event.qualifiers;
    const selectedQual = event?.selectedQualifier;
    if(selectedQual) {
      this.auditService.createAudit(() => `${qualifiers.find((q: OrderableQualifier) => q.name === selectedQual.name) ? 'Ordered' : 'Removed'} ${selectedQual.name} second-half qualifier from ${this.ingredient().name} ingredient from ${this.orderedChoice().name} on ${this.orderedItem().item} item`);
    }

    const tempItem = (this.isSpecial() ? this.orderedItem() : this.itemEditorService.editingItem()?.orderedItem)?.copy();

    const hasIngredient = tempItem.hasIngredient(
      this.choice(),
      this.ingredient()
    );

    const choice: OrderedChoice = tempItem.getChoice(this.orderedChoice().name);

    if (hasIngredient) {
      if (choice) {
        const ingredientName = this.ingredient().name;
        const qualifierNames: string[] = [];
        qualifiers.map((q) => {
          qualifierNames.push(q.name);
        });

        const existingIngredientIndex = choice.ingredients.findIndex(
          (q) => q.ingredient === ingredientName
        );

        if (existingIngredientIndex !== -1) {
          choice.ingredients[existingIngredientIndex].secondHalfQualifiers =
            qualifierNames;
          
          // If no qualifiers selected, unselect the second half
          if (qualifierNames.length === 0) {
            choice.ingredients[existingIngredientIndex].isSecondHalf = false;
            this.selectedSecondSide = null;
            this.selectedSecondHalfQualifiers = this.emptyQualifiers;
          } else {
            this.selectedSecondHalfQualifiers = qualifierNames;
          }
        }
      }
    }
    if(this.isSpecial()){
      this.updatedSpecialItem.emit(tempItem);
    } else {
      this.itemEditorService.updateOrderedItem(tempItem)?.subscribe();
    }
  }

  sideSelection(value: SideSelectionType) {
    const hadSecondHalfSelected =
      this.selectedSecondSide !== null
    const nextSecondSide =
      hadSecondHalfSelected && value === SideSelectionType.LEFT
        ? SideSelectionType.RIGHT
        : hadSecondHalfSelected && value === SideSelectionType.RIGHT
          ? SideSelectionType.LEFT
          : null;

    this.selectedSide = value;
    this.selectedSecondSide = nextSecondSide;

    const tempItem: OrderedItem = (this.isSpecial() ? this.orderedItem() : this.itemEditorService.editingItem()?.orderedItem)?.copy();
    if (tempItem) {
      const tempOrderedIngredient = tempItem['getOrderedIngredient'](
        this.choice(),
        this.ingredient()
      );
      if (tempOrderedIngredient) {
        switch (value) {
          case SideSelectionType.WHOLE: {
            tempOrderedIngredient.isLeftHalf = false;
            tempOrderedIngredient.isRightHalf = false;
            break;
          }
          case SideSelectionType.LEFT: {
            tempOrderedIngredient.isLeftHalf = true;
            tempOrderedIngredient.isRightHalf = false;
            break;
          }
          case SideSelectionType.RIGHT: {
            tempOrderedIngredient.isLeftHalf = false;
            tempOrderedIngredient.isRightHalf = true;
            break;
          }
        }
        tempOrderedIngredient.isSecondHalf = nextSecondSide !== null;

        if (nextSecondSide) {
          const existingQualifiers = this.selectedSecondHalfQualifiers.length
            ? this.selectedSecondHalfQualifiers
            : tempOrderedIngredient.secondHalfQualifiers ?? [];
          tempOrderedIngredient.secondHalfQualifiers = [...existingQualifiers];
          if (!tempOrderedIngredient.secondHalfQualifiers.length && this.ingredient().qualifiers.length > 0) {
            tempOrderedIngredient.secondHalfQualifiers = [this.ingredient().qualifiers[0].name];
          }
        } else {
          tempOrderedIngredient.secondHalfQualifiers = [];
        }
        this.selectedSecondHalfQualifiers = nextSecondSide
          ? tempOrderedIngredient.secondHalfQualifiers ?? this.emptyQualifiers
          : this.emptyQualifiers;

        if(this.isSpecial()){
          this.updatedSpecialItem.emit(tempItem);
        } else {
          this.itemEditorService.updateOrderedItem(tempItem)?.subscribe();
        }
      }
    }
  }

  secondSideSelection(value: SideSelectionType | null) {
    const hasSecondHalfSide =
      value !== null &&
      value !== SideSelectionType.WHOLE &&
      value !== this.selectedSide;
    this.selectedSecondSide = hasSecondHalfSide ? value : null;

    const tempItem: OrderedItem = (this.isSpecial() ? this.orderedItem() : this.itemEditorService.editingItem()?.orderedItem)?.copy();
    if (tempItem) {
      const tempOrderedIngredient = tempItem['getOrderedIngredient'](
        this.choice(),
        this.ingredient()
      );
      if (tempOrderedIngredient) {
        tempOrderedIngredient.isSecondHalf = hasSecondHalfSide;

        // When selecting a second half, auto-set first available qualifier if not already set
        if (hasSecondHalfSide) {
          if (!tempOrderedIngredient.secondHalfQualifiers || tempOrderedIngredient.secondHalfQualifiers.length === 0) {
            if (this.ingredient().qualifiers.length > 0) {
              tempOrderedIngredient.secondHalfQualifiers = [this.ingredient().qualifiers[0].name];
            }
          }
        } else {
          tempOrderedIngredient.secondHalfQualifiers = [];
        }
        this.selectedSecondHalfQualifiers = hasSecondHalfSide
          ? tempOrderedIngredient.secondHalfQualifiers ?? this.emptyQualifiers
          : this.emptyQualifiers;

        if(this.isSpecial()){
          this.updatedSpecialItem.emit(tempItem);
        } else {
          this.itemEditorService.updateOrderedItem(tempItem)?.subscribe();
        }
      }
    }
  }
  
  private syncSecondHalfQualifierSelection(): void {
    this.selectedSecondHalfQualifiers =
      this.orderedIngredient?.isSecondHalf && this.selectedSecondSide
        ? this.orderedIngredient.secondHalfQualifiers ?? this.emptyQualifiers
        : this.emptyQualifiers;
  }

  priceForExtra() {
    return ((this.ingredient().sizePrices.find(s => s.size === this.orderedItem()?.size)?.price ?? 0) * (this.ingredient().qualifiers.find(e=> e.name === 'Extra')?.priceFactor ?? 1));
  }

  toggleTooltip(val: boolean) {
    this.closeAllTooltips = {};
    this.openTooltip = val;
  }
}
